<template>
  <div class="test-bank-manager">
    <!-- Header -->
    <div class="test-bank-header">
      <div class="stats-section">
        <div class="stat-item">
          <span class="stat-value">{{ stats.total }}</span>
          <span class="stat-label">Total</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value comprehension">{{ stats.by_kind?.comprehension || 0 }}</span>
          <span class="stat-label">Comprehension</span>
        </div>
        <div class="stat-item">
          <span class="stat-value application">{{ stats.by_kind?.application || 0 }}</span>
          <span class="stat-label">Application</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value easy">{{ stats.by_difficulty?.easy || 0 }}</span>
          <span class="stat-label">Easy</span>
        </div>
        <div class="stat-item">
          <span class="stat-value medium">{{ stats.by_difficulty?.medium || 0 }}</span>
          <span class="stat-label">Medium</span>
        </div>
        <div class="stat-item">
          <span class="stat-value hard">{{ stats.by_difficulty?.hard || 0 }}</span>
          <span class="stat-label">Hard</span>
        </div>
      </div>
      <div class="actions-section">
        <el-button type="primary" @click="showAIGenerator = true">
          <i class="el-icon-magic-stick"></i> AI Generate
        </el-button>
        <el-button @click="openQuestionEditor(null)">
          <i class="el-icon-plus"></i> Add Question
        </el-button>
      </div>
    </div>

    <!-- Filters -->
    <div class="test-bank-filters">
      <el-input
        v-model="searchQuery"
        placeholder="Search questions..."
        prefix-icon="el-icon-search"
        clearable
        @clear="handleSearch"
        @keyup.enter.native="handleSearch"
        style="width: 300px;"
      />
      <el-select v-model="filterKind" placeholder="Pool" clearable @change="handleSearch">
        <el-option label="All Pools" value="" />
        <el-option label="Comprehension" value="comprehension" />
        <el-option label="Application" value="application" />
      </el-select>
      <el-select v-model="filterDifficulty" placeholder="Difficulty" clearable @change="handleSearch">
        <el-option label="All Difficulties" value="" />
        <el-option label="Easy" value="easy" />
        <el-option label="Medium" value="medium" />
        <el-option label="Hard" value="hard" />
      </el-select>
      <el-select v-model="filterStatus" placeholder="Status" clearable @change="handleSearch">
        <el-option label="All Status" value="" />
        <el-option label="Active" value="active" />
        <el-option label="Draft" value="draft" />
      </el-select>
      <el-button icon="el-icon-refresh" @click="fetchQuestions" :loading="loading">Refresh</el-button>
    </div>

    <!-- Question List -->
    <div class="question-list" v-loading="loading">
      <div v-if="questions.length === 0 && !loading" class="empty-state">
        <i class="el-icon-document"></i>
        <p>No questions yet</p>
        <p class="hint">Create questions manually or generate them with AI</p>
      </div>

      <div v-else class="question-cards">
        <TestBankQuestionCard
          v-for="question in questions"
          :key="question._id?.$oid || question._id"
          :question="question"
          @edit="openQuestionEditor"
          @delete="handleDelete"
        />
      </div>

      <!-- Pagination -->
      <div class="pagination-container" v-if="totalPages > 1">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :total="totalQuestions"
          layout="prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- Question Editor Dialog -->
    <TestBankQuestionEditor
      :visible.sync="showEditor"
      :question="editingQuestion"
      :collection-id="collectionId"
      @save="handleQuestionSaved"
    />

    <!-- AI Generator Dialog -->
    <TestBankAIGenerator
      :visible.sync="showAIGenerator"
      :collection-id="collectionId"
      :collection-form="collectionForm"
      @generated="handleQuestionsGenerated"
    />
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import TestBankQuestionCard from './TestBankQuestionCard.vue'
import TestBankQuestionEditor from './TestBankQuestionEditor.vue'
import TestBankAIGenerator from './TestBankAIGenerator.vue'

export default {
  name: 'TestBankManager',
  components: {
    TestBankQuestionCard,
    TestBankQuestionEditor,
    TestBankAIGenerator
  },
  props: {
    collectionId: {
      type: String,
      required: true
    },
    collectionForm: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      questions: [],
      stats: {
        total: 0,
        by_difficulty: { easy: 0, medium: 0, hard: 0 },
        by_kind: { comprehension: 0, application: 0 },
        by_status: { active: 0, draft: 0 },
        by_source: { ai_generated: 0, manual: 0 }
      },
      searchQuery: '',
      filterKind: '',
      filterDifficulty: '',
      filterStatus: '',
      currentPage: 1,
      pageSize: 10,
      totalQuestions: 0,
      totalPages: 1,
      showEditor: false,
      editingQuestion: null,
      showAIGenerator: false
    }
  },
  watch: {
    collectionId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchQuestions()
          this.fetchStats()
        }
      }
    }
  },
  methods: {
    async fetchQuestions() {
      if (!this.collectionId) return

      this.loading = true
      try {
        const response = await postRequest('getTestBankQuestions', {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
          page: this.currentPage,
          page_size: this.pageSize,
          difficulty: this.filterDifficulty,
          question_kind: this.filterKind,
          status: this.filterStatus,
          search: this.searchQuery
        })

        if (response.data.code === 0) {
          this.questions = response.data.data.questions
          this.totalQuestions = response.data.data.total
          this.totalPages = response.data.data.total_pages
        } else {
          this.$message.error(response.data.msg || 'Failed to load questions')
        }
      } catch (error) {
        console.error('Error fetching questions:', error)
        this.$message.error('Failed to load questions')
      } finally {
        this.loading = false
      }
    },

    async fetchStats() {
      if (!this.collectionId) return

      try {
        const response = await postRequest('getTestBankStats', {
          collection_id: this.collectionId
        })

        if (response.data.code === 0) {
          this.stats = response.data.data
          this.$emit('stats-updated', this.stats)
        }
      } catch (error) {
        console.error('Error fetching stats:', error)
      }
    },

    handleSearch() {
      this.currentPage = 1
      this.fetchQuestions()
    },

    handlePageChange(page) {
      this.currentPage = page
      this.fetchQuestions()
    },

    openQuestionEditor(question) {
      this.editingQuestion = question
      this.showEditor = true
    },

    async handleDelete(question) {
      const questionId = question._id?.$oid || question._id
      try {
        await this.$confirm('Are you sure you want to delete this question?', 'Confirm Delete', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })

        const response = await postRequest('deleteTestBankQuestion', {
          question_id: questionId,
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0) {
          this.$message.success('Question deleted')
          this.fetchQuestions()
          this.fetchStats()
        } else {
          this.$message.error(response.data.msg || 'Failed to delete')
        }
      } catch (e) {
        if (e !== 'cancel') {
          console.error('Error deleting question:', e)
          this.$message.error('Failed to delete question')
        }
      }
    },

    handleQuestionSaved() {
      this.fetchQuestions()
      this.fetchStats()
    },

    handleQuestionsGenerated() {
      this.fetchQuestions()
      this.fetchStats()
    }
  }
}
</script>

<style scoped>
.test-bank-manager {
  padding: 20px;
}

.test-bank-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.stats-section {
  display: flex;
  gap: 24px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.stat-value.easy {
  color: #67c23a;
}

.stat-value.medium {
  color: #e6a23c;
}

.stat-value.hard {
  color: #f56c6c;
}

.stat-value.comprehension {
  color: #409eff;
}

.stat-value.application {
  color: #9b59b6;
}

.stat-divider {
  width: 1px;
  background: #dcdfe6;
  margin: 4px 6px;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.actions-section {
  display: flex;
  gap: 12px;
}

.test-bank-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.question-list {
  min-height: 300px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-state p {
  margin: 8px 0;
}

.empty-state .hint {
  font-size: 13px;
}

.question-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
</style>
